import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Navigation as NavigationIcon,
  TouchApp as ActionIcon,
  Visibility as ViewIcon,
  Help as HelpIcon,
  Person as UserIcon,
  Speed as QuickIcon,
} from '@mui/icons-material';
import { formatShortcut } from '../hooks/useKeyboardShortcuts';

interface ShortcutGroup {
  title: string;
  icon: React.ReactElement;
  shortcuts: {
    key: string;
    ctrl?: boolean;
    alt?: boolean;
    shift?: boolean;
    meta?: boolean;
    description: string;
  }[];
}

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

export const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = React.useState(0);

  const shortcutGroups: ShortcutGroup[] = [
    {
      title: 'Navigation',
      icon: <NavigationIcon />,
      shortcuts: [
        { key: 'd', meta: true, description: 'Go to Dashboard' },
        { key: 'p', meta: true, description: 'Process Documents' },
        { key: 't', meta: true, description: 'Transaction History' },
        { key: 'c', meta: true, description: 'Compliance Dashboard' },
        { key: 'm', meta: true, description: 'Magic Marker' },
        { key: 'a', meta: true, shift: true, description: 'Admin Dashboard' },
      ],
    },
    {
      title: 'Quick Actions',
      icon: <QuickIcon />,
      shortcuts: [
        { key: 'k', meta: true, description: 'Open Quick Search' },
        { key: '/', description: 'Focus Search Field' },
        { key: 'n', meta: true, description: 'New Document' },
        { key: 's', meta: true, description: 'Save Current Work' },
        { key: 'Enter', meta: true, description: 'Submit Form' },
        { key: 'Escape', description: 'Close Modal/Dialog' },
      ],
    },
    {
      title: 'View Controls',
      icon: <ViewIcon />,
      shortcuts: [
        { key: '1', alt: true, description: 'List View' },
        { key: '2', alt: true, description: 'Grid View' },
        { key: 'r', meta: true, description: 'Refresh Page' },
        { key: 'g', description: 'Go to top (press twice)' },
        { key: 'G', shift: true, description: 'Go to bottom (press twice)' },
      ],
    },
    {
      title: 'User & Help',
      icon: <UserIcon />,
      shortcuts: [
        { key: '?', shift: true, description: 'Show Keyboard Shortcuts' },
        { key: 'h', meta: true, description: 'Show Help' },
        { key: 'u', meta: true, description: 'User Profile' },
        { key: 'l', meta: true, shift: true, description: 'Logout' },
      ],
    },
  ];

  const allShortcuts = shortcutGroups.flatMap(group =>
    group.shortcuts.map(s => ({ ...s, group: group.title }))
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon color="primary" />
          <Typography variant="h6">Keyboard Shortcuts</Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Chip
            label={isMac ? 'Mac' : 'Windows/Linux'}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Typography variant="caption" sx={{ ml: 2 }}>
            {isMac ? 'Use ⌘ Command key' : 'Use Ctrl key'} for shortcuts
          </Typography>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="All Shortcuts" />
          {shortcutGroups.map((group, index) => (
            <Tab
              key={group.title}
              label={group.title}
              icon={group.icon}
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          ))}
        </Tabs>

        {activeTab === 0 ? (
          // All Shortcuts View
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Shortcut</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allShortcuts.map((shortcut, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <TableCell>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: theme.palette.grey[100],
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                        }}
                      >
                        {formatShortcut(shortcut)}
                      </Box>
                    </TableCell>
                    <TableCell>{shortcut.description}</TableCell>
                    <TableCell>
                      <Chip label={shortcut.group} size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          // Category View
          <Box>
            {activeTab > 0 && shortcutGroups[activeTab - 1] && (
              <Grid container spacing={2}>
                {shortcutGroups[activeTab - 1].shortcuts.map((shortcut, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Paper
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                      variant="outlined"
                    >
                      <Typography variant="body2">{shortcut.description}</Typography>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 1,
                          backgroundColor: theme.palette.primary.main,
                          color: 'white',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {formatShortcut(shortcut)}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Pro Tips:
          </Typography>
          <Typography variant="caption" component="ul" sx={{ pl: 2 }}>
            <li>Press <kbd>?</kbd> anytime to see this help</li>
            <li>Most shortcuts work globally across all pages</li>
            <li>Press <kbd>gg</kbd> to scroll to top, <kbd>GG</kbd> to scroll to bottom</li>
            <li>Use <kbd>{isMac ? '⌘' : 'Ctrl'}+K</kbd> for quick search anywhere</li>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default KeyboardShortcutsDialog;